import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "usehooks-ts";
import { Scrollbars } from "react-custom-scrollbars";
import { useSelector } from "react-redux";
import format from "date-fns/format";
import { endOfDay, startOfDay } from "date-fns";
import arePropsEqual from "react-fast-compare";
import {
  Box,
  SelectChangeEvent,
  useMediaQuery,
  TextField,
} from "@mui/material";
import Button from "../../../../../../../UI/Buttons/Button";

//styles
import { useStyles } from "./styles";

//utils
import { getDataIds, getGroupByOptions, getPeriod, PeriodTypes } from "../../../../../../../admin/Reports/utils";

//selectors
import {
  selectDealsData,
  selectOptionsData,
  selectPlatformsData
} from "../../../../../../../../store/agent/reports/agentStatisticReport/selectors";

//hooks
import { useAgentStatisticReport } from "../../../../../../../../store/agent/reports/agentStatisticReport/useAgentStatisticReport";
import { usePermissions } from "../../../../../../../../store/common/configuration/usePermissions";
import { useRegistration } from "../../../../../../../../store/common/registration/useRegistration";

//context
import { GlobalContext } from "../../../../../../../../context/GlobalContext";

//constants
import { summaryPeriodOptions } from "../../../../../../../admin/Reports/constant";
import { favoriteFilterKeys, reportStorageProps } from "../../../../constants";

//validation
import { validationTextField as validationTextFieldMax } from "../../../../../../../validation/textFieldMax.validation";

//components
import CSelect from "../../../../../../../UI/Fields/CSelect";
import GroupByField, { IItemOption } from "../../../GroupByField";
import CDateRangePicker from "../../../../../../../UI/Fields/CDateRangePicker";
import FavoriteCheckboxField from "../../../../../../../UI/Fields/FavoriteCheckboxField";
import MuiAutocompleteSelectLazy from "../../../../../../../UI/Fields/MuiAutocompleteSelectLazy";
import { IAgentStatReportFilterDataType as IFilterData} from "../../../../types";
import MuiAutocompleteSelect from "../../../../../../../UI/Fields/MuiAutocompleteSelect";
import MuiTwiceDateRangePicker from "../../../../../../../UI/Fields/MuiTwiceDateRangePicker";
import SaveFilterForm from "../../../../../../../common/FiltersComponents/SaveFilterForm";
import { initialAgentStatReportFilterData } from "../../../DesktopReport";
import MuiChipsInputField from "../../../../../../../UI/Fields/MuiChipsInput";
import {hasRoleLocalization} from "../../../../../../../../constants/roleLocalizationConfig";


interface IFilterProps {
  initialFilter: IFilterData;
  activeField: string;
  setUpdateFilter: (data: any) => void;
  setToggleDrawer: (data: boolean) => void;
  setGenerateReport: React.Dispatch<React.SetStateAction<boolean>>;
  editMode?: boolean;
  filterName?: string;
  onSave?: (data: object) => void;
  setSelectedFilterId?: React.Dispatch<React.SetStateAction<string>>;
}

const Filter: React.FC<IFilterProps> = ({
  initialFilter,
  activeField,
  setUpdateFilter,
  setToggleDrawer,
  setGenerateReport,
  editMode = false,
  filterName = '',
  onSave,
  setSelectedFilterId,
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const mobile = useMediaQuery('(max-width:767px)');
  const { hasPermissions } = usePermissions();

  const hasMyFilters = hasPermissions(["api3.agent.agentcontroller.report_generate"]);

  const context = useContext(GlobalContext);

  const {
    getBrands,
    getCurrencies,
    getPlayers,
    getAgents,
    getAgentRoles,
  } = useAgentStatisticReport();
  const { countries } = useRegistration();

  const optionsData = useSelector(selectOptionsData);
  const dealsData = useSelector(selectDealsData);
  const platformsData = useSelector(selectPlatformsData);

  //state
  const [filter, setFilter] = useState<IFilterData>(editMode ? initialFilter : context.agAgentStatisticReportFilter);
  const [fieldName, setFieldName] = useState<string>(filterName);

  const [dataCountries, setDataCountries] = useState<any | null>(null);

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodTypes>(initialFilter.period);
  const [startDate, setStartDate] = useState<Date | ''>(new Date(initialFilter.sent_from));
  const [endDate, setEndDate] = useState<Date | ''>(new Date(initialFilter.sent_to));

  const [registrationFrom, setRegistrationFrom] = useState<Date | ''>(!!initialFilter.registrationFrom ? new Date(initialFilter.registrationFrom) : '');
  const [registrationTo, setRegistrationTo] = useState<Date | ''>(!!initialFilter.registrationTo ? new Date(initialFilter.registrationTo) : '');

  const [firstDepositFrom, setFirstDepositFrom] = useState<Date | ''>(!!initialFilter.firstDepositFrom ? new Date(initialFilter.firstDepositFrom) : '');
  const [firstDepositTo, setFirstDepositTo] = useState<Date | ''>(!!initialFilter.firstDepositTo ? new Date(initialFilter.firstDepositTo) : '');

  const [cpaQualificationFrom, setCpaQualificationFrom] = useState<Date | ''>(!!initialFilter.cpaQualificationFrom ? new Date(initialFilter.cpaQualificationFrom) : '');
  const [cpaQualificationTo, setCpaQualificationTo] = useState<Date | ''>(!!initialFilter.cpaQualificationTo ? new Date(initialFilter.cpaQualificationTo) : '');

  const [groupBy, setGroupBy] = useState<IItemOption[]>(editMode ? initialFilter.group_by.fields : context.agAgentStatisticReportFilter.group_by.fields);

  //favorite fields state
  const [favAdmSRAgentIds, setFavAdmSRAgentIds] = useLocalStorage<boolean>(favoriteFilterKeys.agentIds, false);
  const [favAdmSRAgentRoles, setFavAdmSRAgentRoles] = useLocalStorage<boolean>(favoriteFilterKeys.agentRoles, false);
  const [favAdmSRProductIds, setFavAdmSRProductIds] = useLocalStorage<boolean>(favoriteFilterKeys.productIds, false);
  const [favAdmSRPlatformIds, setFavAdmSRPlatformIds] = useLocalStorage<boolean>(favoriteFilterKeys.platformIds, false);
  const [favAdmSRManagerIds, setFavAdmSRManagerIds] = useLocalStorage<boolean>(favoriteFilterKeys.managerIds, false);
  const [favAdmSRAffiliateIds, setFavAdmSRAffiliateIds] = useLocalStorage<boolean>(favoriteFilterKeys.affiliateIds, false);
  const [favAdmSRTrackerIds, setFavAdmSRTrackerIds] = useLocalStorage<boolean>(favoriteFilterKeys.trackerIds, false);
  const [favAdmSRCreativeIds, setFavAdmSRCreativeIds] = useLocalStorage<boolean>(favoriteFilterKeys.creativeIds, false);
  const [favAdmSRBrands, setFavAdmSRBrands] = useLocalStorage<boolean>(favoriteFilterKeys.brands, false);
  const [favAdmSRCurrencies, setFavAdmSRCurrencies] = useLocalStorage<boolean>(favoriteFilterKeys.currencies, false);
  const [favAdmSRDealTypes, setFavAdmSRDealTypes] = useLocalStorage<boolean>(favoriteFilterKeys.dealTypes, false);
  const [favAdmSRAnid, setFavAdmSRAnid] = useLocalStorage<boolean>(favoriteFilterKeys.anid, false);
  const [favAdmSRPlayerId, setFavAdmSRPlayerId] = useLocalStorage<boolean>(favoriteFilterKeys.playerId, false);
  const [favAdmSRSource1, setFavAdmSRSource1] = useLocalStorage<boolean>(favoriteFilterKeys.source1, false);
  const [favAdmSRSource2, setFavAdmSRSource2] = useLocalStorage<boolean>(favoriteFilterKeys.source2, false);
  const [favAdmSRSource3, setFavAdmSRSource3] = useLocalStorage<boolean>(favoriteFilterKeys.source3, false);
  const [favAdmSRSource4, setFavAdmSRSource4] = useLocalStorage<boolean>(favoriteFilterKeys.source4, false);
  const [favAdmSRSource5, setFavAdmSRSource5] = useLocalStorage<boolean>(favoriteFilterKeys.source5, false);
  const [favAdmSRDeviceTypes, setFavAdmSRDeviceTypes] = useLocalStorage<boolean>(favoriteFilterKeys.deviceTypes, false);
  const [favAdmSRGeo, setFavAdmSRGeo] = useLocalStorage<boolean>(favoriteFilterKeys.geo, false);

  useEffect(() => {
    if (!!selectedPeriod) {
      const period = getPeriod(selectedPeriod);

      if (!!period.start && !!period.end) {
        setStartDate(period.start);
        setEndDate(period.end);
        setFilter((prevState: any) => {
          return {
            ...prevState,
            period: selectedPeriod,
            sent_from: format(startOfDay(new Date(period.start)), "yyyy-MM-dd HH:mm:ss"),
            sent_to: format(endOfDay(new Date(period.end)), "yyyy-MM-dd HH:mm:ss"),
          }
        })
      }
    }
  }, [selectedPeriod]);

  useEffect(() => {
    setSelectedPeriod(filter.period);
  }, [filter.period]);

  useEffect(() => {
    if (!editMode) {
      context.setAgAgentStatisticReportFilter(filter);
    }
  }, [filter]);

  useEffect(() => {
    setFilter((prevState) => {
      return {
        ...prevState,
        group_by: {
          fields: groupBy,
        },
      }
    })
  }, [groupBy]);

  useEffect(() => {
    if (!!countries.data) {
      const data = countries.data.reduce((acc: any[], item: any) => {
        acc.push({
          id: item.code,
          value: item.code,
        });
        return acc;
      }, []);
      setDataCountries({
        list: data,
        total_entries: data.length,
      });
    }
  }, [countries, i18n.language]);

  const handleChangeDates = (start: Date, end: Date) => {
    const noEndDate = !end;

    setStartDate(start);
    setEndDate(noEndDate ? start : end);
    setFilter((prevState: any) => {
      return {
        ...prevState,
        period: '',
        sent_from: format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss"),
        sent_to: format(endOfDay(new Date(noEndDate ? start : end)), "yyyy-MM-dd HH:mm:ss"),
      }
    });
  };

  const handleChangePeriodSelect = (event: SelectChangeEvent) => {
    //@ts-ignore
    const value: PeriodTypes = event.target.value;
    setSelectedPeriod(value);
  };

  const handleApplyFilter = useCallback(() => {
    setUpdateFilter(filter);
    setToggleDrawer(false);

    if (!editMode) {
      if (typeof setSelectedFilterId === 'function') {
        setSelectedFilterId('');
      }
      setTimeout(() => {
        setGenerateReport((prevState) => {
          return !prevState
        });
      }, 120);
    }
  }, [filter]);

  const fieldFilterPayload = () => {
    return {
      page: 1,
      search: null,
      perPage: 100,
    }
  };

  const fieldFilterOncePayload = () => {
    return {
      page: 1,
      search: null,
      perPage: 100,
    }
  };

  const handleChangeRegistrationDate = (start: Date | null, end: Date | null) => {
    setRegistrationFrom(!!start ? start : '');
    setRegistrationTo(!!end ? end : '');

    setFilter((prevState: any) => {
      return {
        ...prevState,
        registrationFrom: !!start ? format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss") : '',
        registrationTo: !!end ? format(endOfDay(new Date(end)), "yyyy-MM-dd HH:mm:ss") : '',
      }
    })
  }

  const handleChangeFirstDepositDate = (start: Date | null, end: Date | null) => {
    setFirstDepositFrom(!!start ? start : '');
    setFirstDepositTo(!!end ? end : '');

    setFilter((prevState: any) => {
      return {
        ...prevState,
        firstDepositFrom: !!start ? format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss") : '',
        firstDepositTo: !!end ? format(endOfDay(new Date(end)), "yyyy-MM-dd HH:mm:ss") : '',
      }
    })
  }

  const handleChangeCpaQualificationDate = (start: Date | null, end: Date | null) => {
    setCpaQualificationFrom(!!start ? start : '');
    setCpaQualificationTo(!!end ? end : '');

    setFilter((prevState: any) => {
      return {
        ...prevState,
        cpaQualificationFrom: !!start ? format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss") : '',
        cpaQualificationTo: !!end ? format(endOfDay(new Date(end)), "yyyy-MM-dd HH:mm:ss") : '',
      }
    })
  }

  const validateName = validationTextFieldMax(fieldName, 255, {
    required: t("common.validation.required_field"),
    maxLength: t("common.validation.max_length"),
  });

  const handleSave = useCallback(() => {
    if (typeof onSave === 'function') {
      onSave({
        name: fieldName,
        filter: filter,
      })
    }
  }, [filter, fieldName]);

  return (<>
    {!!dataCountries && !!optionsData && (<>
      <Box className={styles.wrapper}>
        <Scrollbars
          className={styles.customScroll}
          style={{ overflow: "hidden" }}
          hideTracksWhenNotNeeded={true}
          renderView={props => <div {...props} className="view"/>}
          renderTrackHorizontal={props => <div {...props} className="track-horizontal"/>}
          renderTrackVertical={props => <div {...props} className="track-vertical"/>}
        >
          {editMode && (
            <Box className={styles.box}>
              <TextField
                className={styles.fieldFull}
                size="small"
                id="filter_name"
                name="filter_name"
                type="text"
                label={t(`common.forms.fields.filter_name`)}
                value={fieldName}
                error={!!validateName}
                helperText={!!validateName ? validateName : ''}
                onChange={(event) => {
                  setFieldName(event.target.value);
                }}
              />
            </Box>
          )}
          {(<>
            {mobile && (<>
              <Box className={styles.box}>
                <CSelect
                  name="period"
                  label={t("admin.reports.filter.period")}
                  value={selectedPeriod}
                  options={summaryPeriodOptions}
                  onChange={handleChangePeriodSelect}
                  prefix="common.components.periods."
                />
              </Box>

              <Box className={`${styles.box} noMargin`}>
                <CDateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleChangeDates}
                />
              </Box>

              <Box className={`${styles.box} noMargin`}>
                <GroupByField
                  name="groupBy"
                  value={groupBy}
                  options={getGroupByOptions(optionsData.group_by)}
                  setFilter={setGroupBy}
                  prefix={"admin.reports.filter.group_by_select."}
                />
              </Box>
            </>)}

            {/*AgentIds*/}
            {optionsData.available_filters.includes("agentIds") && (
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRAgentIds}
                        setChecked={setFavAdmSRAgentIds}
                        fieldName={t(`admin.reports.filter.agentIds`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiAutocompleteSelectLazy
                      keyValue="value"
                      keyId="id"
                      id="agentIds"
                      name="agentIds"
                      label={t(`admin.reports.filter.agentIds`)}
                      value={filter.agentIds}
                      multiple={true}
                      showSelectAll={false}
                      disabled={false}
                      getOptions={getAgents}
                      payloadData={fieldFilterPayload()}
                      labelWithId={true}
                      onChange={setFilter}
                      disableCloseOnSelect={true}
                      autoFocus={activeField === 'agentIds'}
                      showLabelCounter={true}
                    />
                  </Box>
                </Box>
              </Box>
            )}
            {/*agentRoles*/}
            {optionsData.available_filters.includes("agentRoleIds") && (
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRAgentRoles}
                        setChecked={setFavAdmSRAgentRoles}
                        fieldName={t(`admin.reports.filter.agentRoles`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiAutocompleteSelectLazy
                      keyValue="value"
                      keyId="id"
                      id="agentRoles"
                      name="agentRoles"
                      label={t(`admin.reports.filter.agentRoles`)}
                      value={filter.agentRoles}
                      multiple={true}
                      showSelectAll={false}
                      disabled={false}
                      getOptions={getAgentRoles}
                      payloadData={fieldFilterPayload()}
                      labelWithId={true}
                      onChange={setFilter}
                      disableCloseOnSelect={true}
                      autoFocus={activeField === 'agentRoles'}
                      showLabelCounter={true}
                      {...(hasRoleLocalization() ? { prefix: "common.roles." } : {})}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/*GEO*/}
            {optionsData.available_filters.includes("geos") && (<>
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRGeo}
                        setChecked={setFavAdmSRGeo}
                        fieldName={t(`admin.reports.filter.geo`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiAutocompleteSelect
                      keyValue="value"
                      keyId="id"
                      id="geo"
                      name="geo"
                      label={t(`admin.reports.filter.geo`)}
                      value={filter.geo}
                      multiple={true}
                      showSelectAll={false}
                      disabled={false}
                      staticOptions={dataCountries.list}
                      onChange={setFilter}
                      disableCloseOnSelect={true}
                      autoFocus={activeField === 'geo'}
                      showLabelCounter={true}
                      prefix={`common.components.creative.list_geolocales.`}
                    />
                  </Box>
                </Box>
              </Box>
            </>)}

            {/*Brands*/}
            {optionsData.available_filters.includes("brands") && (
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRBrands}
                        setChecked={setFavAdmSRBrands}
                        fieldName={t(`admin.reports.filter.brands`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiAutocompleteSelectLazy
                      keyValue="value"
                      keyId="id"
                      id="brands"
                      name="brands"
                      label={t(`admin.reports.filter.brands`)}
                      value={filter.brands}
                      multiple={true}
                      showSelectAll={false}
                      disabled={false}
                      getOptions={getBrands}
                      payloadData={fieldFilterOncePayload()}
                      labelWithId={true}
                      onChange={setFilter}
                      disableCloseOnSelect={true}
                      autoFocus={activeField === 'brands'}
                      showLabelCounter={true}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/*Currency*/}
            {optionsData.available_filters.includes("currencies") && (
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRCurrencies}
                        setChecked={setFavAdmSRCurrencies}
                        fieldName={t(`admin.reports.filter.currencies`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiAutocompleteSelectLazy
                      keyValue="value"
                      keyId="id"
                      id="currencies"
                      name="currencies"
                      label={t(`admin.reports.filter.currencies`)}
                      value={filter.currencies}
                      multiple={true}
                      showSelectAll={false}
                      disabled={false}
                      getOptions={getCurrencies}
                      payloadData={fieldFilterOncePayload()}
                      labelWithId={false}
                      onChange={setFilter}
                      disableCloseOnSelect={true}
                      autoFocus={activeField === 'currencies'}
                      showLabelCounter={true}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/*playerIds*/}
            {optionsData.available_filters.includes("playerIds") && (
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRPlayerId}
                        setChecked={setFavAdmSRPlayerId}
                        fieldName={t(`admin.reports.filter.playerId`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiAutocompleteSelectLazy
                      keyValue="value"
                      keyId="id"
                      id="playerId"
                      name="playerId"
                      label={t(`admin.reports.filter.playerId`)}
                      value={filter.playerId}
                      multiple={true}
                      showSelectAll={false}
                      disabled={false}
                      getOptions={getPlayers}
                      payloadData={fieldFilterOncePayload()}
                      labelWithId={false}
                      onChange={setFilter}
                      disableCloseOnSelect={true}
                      autoFocus={activeField === 'playerId'}
                      showLabelCounter={true}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {optionsData.available_filters.includes("firstDepositPeriod") && (<>
              <Box className={styles.box}>
                <Box className={`${styles.fieldWrapper} ${!editMode ? styles.fieldWrapperPadding : ''}`}>
                  <Box className={styles.field}>
                    <MuiTwiceDateRangePicker
                      label={'admin.reports.filter.firstDepositDate'}
                      startLabel={'admin.reports.filter.from'}
                      endLabel={'admin.reports.filter.to'}
                      startDate={firstDepositFrom}
                      endDate={firstDepositTo}
                      onChange={handleChangeFirstDepositDate}
                    />
                  </Box>
                </Box>
              </Box>
            </>)}

            {/*`agentRegistrationDate*/}
            {optionsData.available_filters.includes("agentRegistrationPeriod") && (<>
              <Box className={styles.box}>
                <Box className={`${styles.fieldWrapper} ${!editMode ? styles.fieldWrapperPadding : ''}`}>
                  <Box className={styles.field}>
                    <MuiTwiceDateRangePicker
                      label={'admin.reports.filter.agentRegistrationDate'}
                      startLabel={'admin.reports.filter.from'}
                      endLabel={'admin.reports.filter.to'}
                      startDate={registrationFrom}
                      endDate={registrationTo}
                      onChange={handleChangeRegistrationDate}
                    />
                  </Box>
                </Box>
              </Box>
            </>)}

            {/*
            {optionsData.available_filters.includes("cpaQualificationPeriod") && (<>
              <Box className={styles.box}>
                <Box className={`${styles.fieldWrapper} ${!editMode ? styles.fieldWrapperPadding : ''}`}>
                  <Box className={styles.field}>
                    <MuiTwiceDateRangePicker
                      label={'admin.reports.filter.cpaQualificationPeriod'}
                      startLabel={'admin.reports.filter.from'}
                      endLabel={'admin.reports.filter.to'}
                      startDate={cpaQualificationFrom}
                      endDate={cpaQualificationTo}
                      onChange={handleChangeCpaQualificationDate}
                    />
                  </Box>
                </Box>
              </Box>
            </>)}

            {optionsData.available_filters.includes("anid") && (<>
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRAnid}
                        setChecked={setFavAdmSRAnid}
                        fieldName={t(`admin.reports.filter.anid`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiChipsInputField
                      id="anid"
                      name="anid"
                      label={t(`admin.reports.filter.anid`)}
                      value={filter.anid}
                      onChange={setFilter}
                      autoFocus={activeField === 'anid'}
                    />
                  </Box>
                </Box>
              </Box>
            </>)}

            {optionsData.available_filters.includes("source1") && (<>
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRSource1}
                        setChecked={setFavAdmSRSource1}
                        fieldName={t(`admin.reports.filter.source1`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiChipsInputField
                      id="source1"
                      name="source1"
                      label={t(`admin.reports.filter.source1`)}
                      value={filter.source1}
                      onChange={setFilter}
                      autoFocus={activeField === 'source1'}
                    />
                  </Box>
                </Box>
              </Box>
            </>)}

            {optionsData.available_filters.includes("source2") && (<>
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRSource2}
                        setChecked={setFavAdmSRSource2}
                        fieldName={t(`admin.reports.filter.source2`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiChipsInputField
                      id="source2"
                      name="source2"
                      label={t(`admin.reports.filter.source2`)}
                      value={filter.source2}
                      onChange={setFilter}
                      autoFocus={activeField === 'source2'}
                    />
                  </Box>
                </Box>
              </Box>
            </>)}

            {optionsData.available_filters.includes("source3") && (<>
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRSource3}
                        setChecked={setFavAdmSRSource3}
                        fieldName={t(`admin.reports.filter.source3`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiChipsInputField
                      id="source3"
                      name="source3"
                      label={t(`admin.reports.filter.source3`)}
                      value={filter.source3}
                      onChange={setFilter}
                      autoFocus={activeField === 'source3'}
                    />
                  </Box>
                </Box>
              </Box>
            </>)}

            {optionsData.available_filters.includes("source4") && (<>
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRSource4}
                        setChecked={setFavAdmSRSource4}
                        fieldName={t(`admin.reports.filter.source4`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiChipsInputField
                      id="source4"
                      name="source4"
                      label={t(`admin.reports.filter.source4`)}
                      value={filter.source4}
                      onChange={setFilter}
                      autoFocus={activeField === 'source4'}
                    />
                  </Box>
                </Box>
              </Box>
            </>)}

            {optionsData.available_filters.includes("source5") && (<>
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRSource5}
                        setChecked={setFavAdmSRSource5}
                        fieldName={t(`admin.reports.filter.source5`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiChipsInputField
                      id="source5"
                      name="source5"
                      label={t(`admin.reports.filter.source5`)}
                      value={filter.source5}
                      onChange={setFilter}
                      autoFocus={activeField === 'source5'}
                    />
                  </Box>
                </Box>
              </Box>
            </>)}

            {optionsData.available_filters.includes("deviceTypes") && (
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSRDeviceTypes}
                        setChecked={setFavAdmSRDeviceTypes}
                        fieldName={t(`admin.reports.filter.deviceTypes`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiAutocompleteSelectLazy
                      keyValue="value"
                      keyId="id"
                      id="deviceTypes"
                      name="deviceTypes"
                      label={t(`admin.reports.filter.deviceTypes`)}
                      value={filter.deviceTypes}
                      multiple={true}
                      showSelectAll={false}
                      disabled={false}
                      getOptions={getDeviceTypes}
                      payloadData={fieldPayload()}
                      labelWithId={false}
                      onChange={setFilter}
                      disableCloseOnSelect={true}
                      autoFocus={activeField === 'deviceTypes'}
                      showLabelCounter={true}
                    />
                  </Box>
                </Box>
              </Box>
            )}
            */}

            {!editMode && hasMyFilters && (
              <SaveFilterForm
                filter={{
                  ...filter,
                  geo: !!filter.geo?.length ? filter.geo.map((item) => {
                    return {
                      id: item.id,
                      value: item.id,
                    }
                  }) : []
                }}
                type={reportStorageProps.type}
                target={reportStorageProps.target}
                exceptions={reportStorageProps.exceptions}
                exceptionsIds={reportStorageProps.exceptionsIds}
                disabled={
                  arePropsEqual(
                    {
                      ...initialAgentStatReportFilterData,
                      sent_from: format(new Date(initialAgentStatReportFilterData.sent_from), "yyyy-MM-dd HH:mm:ss"),
                      sent_to: format(new Date(initialAgentStatReportFilterData.sent_to), "yyyy-MM-dd HH:mm:ss"),
                    },
                    {
                      ...filter,
                      sent_from: format(new Date(filter.sent_from), "yyyy-MM-dd HH:mm:ss"),
                      sent_to: format(new Date(filter.sent_to), "yyyy-MM-dd HH:mm:ss"),
                    }
                  )
                }
              />
            )}
          </>)}
        </Scrollbars>
      </Box>
      <Box className={styles.actions}>
        <Box className={styles.actionsWrapper}>
          {editMode ? (
            <Button
              gradient
              className={styles.button}
              fullWidth
              disableElevation
              type="submit"
              variant="contained"
              onClick={handleSave}
              disabled={
                arePropsEqual({
                  name: filterName,
                  ...initialFilter,
                  sent_from: format(new Date(initialFilter.sent_from), "yyyy-MM-dd HH:mm:ss"),
                  sent_to: format(new Date(initialFilter.sent_to), "yyyy-MM-dd HH:mm:ss"),
                }, {
                  name: fieldName,
                  ...filter,
                  sent_from: format(new Date(filter.sent_from), "yyyy-MM-dd HH:mm:ss"),
                  sent_to: format(new Date(filter.sent_to), "yyyy-MM-dd HH:mm:ss"),
                }) || !!validateName
              }
            >
              {t("common.buttons.save")}
            </Button>
          ) : (
            <Button
              gradient
              className={styles.button}
              fullWidth
              disableElevation
              type="submit"
              variant="contained"
              onClick={handleApplyFilter}
              disabled={arePropsEqual(initialFilter, filter)}
            >
              {t("common.buttons.apply")}
            </Button>
          )}
          <Button
            className={styles.button}
            color="secondary"
            disableElevation
            type="button"
            variant="outlined"
            onClick={() => setToggleDrawer(false)}
          >
            {t("common.buttons.cancel")}
          </Button>
        </Box>
      </Box>
    </>)}
  </>);
};

export default Filter;
